/**
 * Capabilities Service
 * Fetches the user capability matrix from Supabase site_settings.
 * Keys are stored as: capability_{path}_{role}  (e.g. capability_/clients_staff)
 * Super Admin always has full access regardless of the matrix.
 */

import { createClient } from '@/lib/supabase/client';

export type CapabilityRole = 'client' | 'staff' | 'superadmin';

/** Map of page-path → role → allowed */
export type CapabilityMatrix = Record<string, Record<CapabilityRole, boolean>>;

const CAPABILITIES_STORAGE_KEY = 'celsoft_user_capabilities';

/** Default fallback matrix (mirrors DEFAULT_CAPABILITIES in SuperAdminContent) */
const DEFAULT_MATRIX: CapabilityMatrix = {
  '/':                             { client: true,  staff: true,  superadmin: true },
  '/landing-v2':                   { client: true,  staff: true,  superadmin: true },
  '/landing-v3':                   { client: true,  staff: true,  superadmin: true },
  '/landing-v4':                   { client: true,  staff: true,  superadmin: true },
  '/landing-v5':                   { client: true,  staff: true,  superadmin: true },
  '/sign-up-login-screen':         { client: true,  staff: true,  superadmin: true },
  '/engagement-guide':             { client: true,  staff: true,  superadmin: true },
  '/service-product-detail-page':  { client: true,  staff: true,  superadmin: true },
  '/activity-feed':                { client: true,  staff: true,  superadmin: true },
  '/client-portal':                { client: true,  staff: false, superadmin: true },
  '/staff-admin-dashboard':        { client: false, staff: true,  superadmin: true },
  '/new-registrations':            { client: false, staff: true,  superadmin: true },
  '/clients':                      { client: false, staff: true,  superadmin: true },
  '/super-admin-panel':            { client: false, staff: false, superadmin: true },
};

/**
 * Fetch the full capability matrix from Supabase.
 * Falls back to localStorage, then to the hard-coded defaults.
 */
export async function fetchCapabilityMatrix(): Promise<CapabilityMatrix> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('site_settings')
      .select('key, value')
      .like('key', 'capability_%');

    if (!error && data && data.length > 0) {
      // Build matrix from DB rows
      const matrix: CapabilityMatrix = JSON.parse(JSON.stringify(DEFAULT_MATRIX));

      data.forEach((row: { key: string; value: string }) => {
        // key format: capability_{path}_{role}
        // path may contain slashes, role is the last segment
        const withoutPrefix = row.key.replace(/^capability_/, '');
        const roles: CapabilityRole[] = ['client', 'staff', 'superadmin'];
        let matchedRole: CapabilityRole | null = null;
        let path = '';

        for (const role of roles) {
          if (withoutPrefix.endsWith(`_${role}`)) {
            matchedRole = role;
            path = withoutPrefix.slice(0, -(role.length + 1));
            break;
          }
        }

        if (matchedRole && path) {
          if (!matrix[path]) {
            matrix[path] = { client: false, staff: false, superadmin: true };
          }
          matrix[path][matchedRole] = row.value === 'true';
        }
      });

      // Cache to localStorage for offline fallback
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(CAPABILITIES_STORAGE_KEY, JSON.stringify(matrix));
        } catch { /* ignore */ }
      }

      return matrix;
    }
  } catch {
    // fall through to localStorage
  }

  // Fallback: localStorage
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(CAPABILITIES_STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_MATRIX, ...JSON.parse(stored) };
      }
    } catch { /* ignore */ }
  }

  return DEFAULT_MATRIX;
}

/**
 * Check whether a specific role is allowed to access a page path,
 * using the live capability matrix.
 * Super Admin always returns true.
 */
export function isCapabilityAllowed(
  matrix: CapabilityMatrix,
  path: string,
  role: string | null | undefined
): boolean {
  if (role === 'superadmin') return true;
  if (!role) return false;

  const pageMatrix = matrix[path] ?? DEFAULT_MATRIX[path];
  if (!pageMatrix) return false;

  return pageMatrix[role as CapabilityRole] ?? false;
}
